import React, { useMemo, useRef, useState } from "react";
import Button from "../../components/FormComponents/Button";
import Input from "../../components/FormComponents/Input";
import MaterialIcon from "../../components/BuilderComponents/MaterialIcon";
import CopyToClipboard from "../../components/BuilderComponents/CopyToClipboard";
import { toastError, toastSuccess } from "../../utils/toastUtils";
import {
  getKeyVaultAuditLogsApi,
  getKeyVaultSecretApi,
  getKeyVaultSecretsApi,
  updateKeyVaultSecretApi
} from "../../services/apis/keyVaultApis";
import "./KeyVault.css";

const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
};

const shortVersion = (v) => (v ? `${v.slice(0, 8)}…` : "(new)");

// Constrain the editable key field to the Key Vault naming convention used
// here: UPPERCASE letters, digits and dashes only. Lowercase is auto-capitalized
// and any other character (spaces, underscores, symbols) is dropped. Applies to
// the new-secret name field, not to existing keys (which are shown verbatim).
const sanitizeKeyInput = (raw) => raw.toUpperCase().replace(/[^A-Z0-9-]/g, "");

const MASK = "••••••••••";

const AUDIT_PAGE_SIZE = 20;

// Platform-level Azure Key Vault env-secret editor.
// Left panel  : results of an on-demand search (scoped by the backend prefix),
//               masked, with per-secret show toggles. Nothing is fetched until
//               the user searches; each result can be moved right for editing.
// Right panel : the moved secrets, editable. "Push changes" updates ONLY the
//               right-side secrets whose value was modified.
const KeyVaultSection = () => {
  const [secrets, setSecrets] = useState([]); // [{ key, name }] -> latest search results
  const [revealed, setRevealed] = useState({}); // { [key]: value } -> presence means "shown"
  const [search, setSearch] = useState(""); // text in the search box
  const [searching, setSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [rightItems, setRightItems] = useState([]); // [{ key, value, original }]
  const [pushing, setPushing] = useState(false);
  const [activeTab, setActiveTab] = useState("secrets"); // "secrets" | "history"
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditSearch, setAuditSearch] = useState("");
  const [auditPage, setAuditPage] = useState(1);
  const [auditMeta, setAuditMeta] = useState({ total_count: 0, total_pages: 0, page: 1, limit: AUDIT_PAGE_SIZE });

  const searchAbortRef = useRef(null); // AbortController for the in-flight search
  const idCounterRef = useRef(0); // stable ids for right-panel rows (new rows have no key yet)
  const nextId = () => {
    idCounterRef.current += 1;
    return idCounterRef.current;
  };

  // keys already in the edit panel are hidden from the left list (ignore blank
  // keys belonging to not-yet-named new secrets)
  const movedKeys = useMemo(() => new Set(rightItems.filter((i) => i.key).map((i) => i.key)), [rightItems]);

  // Run a server-side search. A term is required (the backend rejects empty).
  // Aborts any previous in-flight search before starting a new one.
  const runSearch = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    const q = search.trim();
    if (!q) {
      toastError("Type a search term first");
      return;
    }
    if (searchAbortRef.current) searchAbortRef.current.abort();
    const controller = new AbortController();
    searchAbortRef.current = controller;
    setSearching(true);
    setHasSearched(true);
    try {
      const { data } = await getKeyVaultSecretsApi({ search: q, signal: controller.signal });
      setSecrets(Array.isArray(data) ? data : []);
    } catch (error) {
      // aborted searches and other errors are intentionally swallowed here;
      // apiService surfaces non-abort errors via toast
    } finally {
      // only clear the spinner if this is still the active search
      if (searchAbortRef.current === controller) {
        setSearching(false);
        searchAbortRef.current = null;
      }
    }
  };

  const cancelSearch = () => {
    if (searchAbortRef.current) {
      searchAbortRef.current.abort();
      searchAbortRef.current = null;
    }
    setSearching(false);
  };

  const fetchAuditLogs = async (page = 1, searchTerm = auditSearch) => {
    setAuditLoading(true);
    try {
      const { data } = await getKeyVaultAuditLogsApi({ page, limit: AUDIT_PAGE_SIZE, search: searchTerm.trim() });
      setAuditLogs(Array.isArray(data?.logs) ? data.logs : []);
      setAuditMeta(data?.meta || { total_count: 0, total_pages: 0, page, limit: AUDIT_PAGE_SIZE });
      setAuditPage(page);
    } catch (error) {
      // apiService already surfaces the error toast
    } finally {
      setAuditLoading(false);
    }
  };

  const runAuditSearch = (e) => {
    if (e?.preventDefault) e.preventDefault();
    fetchAuditLogs(1, auditSearch);
  };

  const goAuditPage = (p) => {
    if (p < 1 || p > (auditMeta.total_pages || 1) || p === auditPage || auditLoading) return;
    fetchAuditLogs(p, auditSearch);
  };

  const selectTab = (tab) => {
    setActiveTab(tab);
    if (tab === "history") fetchAuditLogs(1, auditSearch);
  };

  // Left list = current search results minus anything already moved to edit.
  const filteredLeft = useMemo(() => secrets.filter((s) => !movedKeys.has(s.key)), [secrets, movedKeys]);

  // Fetch (and cache) a single secret's value; returns it.
  const revealOne = async (key) => {
    if (revealed[key] !== undefined) return revealed[key];
    const { data } = await getKeyVaultSecretApi({ key });
    const value = data?.value ?? "";
    setRevealed((prev) => ({ ...prev, [key]: value }));
    return value;
  };

  const toggleShowOne = async (key) => {
    if (revealed[key] !== undefined) {
      setRevealed((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      return;
    }
    try {
      await revealOne(key);
    } catch (error) {
      /* handled by apiService */
    }
  };

  const moveRight = async (key) => {
    try {
      const value = await revealOne(key);
      setRightItems((prev) => [...prev, { id: nextId(), key, value, original: value, isNew: false }]);
    } catch (error) {
      /* handled by apiService */
    }
  };

  // Add a blank new secret to the edit panel (editable key + value).
  const addNewSecret = () => {
    setRightItems((prev) => [...prev, { id: nextId(), key: "", value: "", original: "", isNew: true }]);
  };

  const removeRight = (id) => {
    setRightItems((prev) => prev.filter((i) => i.id !== id));
  };

  const editValue = (id, value) => {
    setRightItems((prev) => prev.map((i) => (i.id === id ? { ...i, value } : i)));
  };

  const editKey = (id, key) => {
    const clean = sanitizeKeyInput(key);
    setRightItems((prev) => prev.map((i) => (i.id === id ? { ...i, key: clean } : i)));
  };

  const resetRight = (id) => {
    setRightItems((prev) => prev.map((i) => (i.id === id ? { ...i, value: i.original } : i)));
  };

  // A row is "pending" (will be pushed) when an existing secret's value changed,
  // or a new secret has both a key and a value.
  const isPending = (i) => (i.isNew ? i.key.trim() !== "" && i.value !== "" : i.value !== i.original);

  const pushChanges = async () => {
    const pending = rightItems.filter(isPending);
    if (pending.length === 0) {
      toastError("Nothing to push — edit a value or name a new secret first");
      return;
    }
    // guard duplicate keys among the pending set (e.g. two new rows, same name)
    const keys = pending.map((i) => i.key);
    if (new Set(keys).size !== keys.length) {
      toastError("Duplicate secret keys in the edit list — each key must be unique");
      return;
    }
    setPushing(true);
    try {
      for (const item of pending) {
        await updateKeyVaultSecretApi({ key: item.key, value: item.value });
      }
      // commit: new value becomes the baseline; new secrets become normal rows
      const pushedIds = new Set(pending.map((p) => p.id));
      setRightItems((prev) => prev.map((i) => (pushedIds.has(i.id) ? { ...i, original: i.value, isNew: false } : i)));
      setRevealed((prev) => {
        const next = { ...prev };
        pending.forEach((p) => {
          if (next[p.key] !== undefined) next[p.key] = p.value;
        });
        return next;
      });
      toastSuccess(`Pushed ${pending.length} secret${pending.length > 1 ? "s" : ""} to Key Vault`);
      if (activeTab === "history") fetchAuditLogs(auditPage, auditSearch);
    } catch (error) {
      /* handled by apiService */
    } finally {
      setPushing(false);
    }
  };

  const dirtyCount = rightItems.filter(isPending).length;

  return (
    <section className="keyvault_section">
      <div className="keyvault_header">
        <div>
          <h2 className="keyvault_title">Azure Key Vault — Environment Secrets</h2>
          <p className="keyvault_subtitle">
            Platform-level secrets. Move a secret to the right to edit it, then push your changes.
          </p>
        </div>
      </div>

      <div className="keyvault_tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "secrets"}
          className={`keyvault_tab ${activeTab === "secrets" ? "active" : ""}`}
          onClick={() => selectTab("secrets")}
        >
          <MaterialIcon name="key" className="keyvault_btn_icon" /> Secrets
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "history"}
          className={`keyvault_tab ${activeTab === "history" ? "active" : ""}`}
          onClick={() => selectTab("history")}
        >
          <MaterialIcon name="history" className="keyvault_btn_icon" /> Change History
        </button>
      </div>

      {activeTab === "secrets" ? (
        <div className="keyvault_panels">
          {/* LEFT — search results */}
          <div className="keyvault_panel">
            <div className="keyvault_panel_head">
              <span className="keyvault_panel_title">
                Vault Secrets
                <span className="keyvault_count">{filteredLeft.length}</span>
              </span>
            </div>

            <form className="keyvault_search" onSubmit={runSearch}>
              <Input
                placeholder="Search secrets by name…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                disabled={searching}
                containerStyle={{ width: "100%" }}
              />
              <Button type="submit" variant="primary" disabled={searching || !search.trim()}>
                <MaterialIcon name={searching ? "hourglass_top" : "search"} className="keyvault_btn_icon" />
                {searching ? "Searching…" : "Search"}
              </Button>
              {searching ? (
                <Button type="button" variant="secondary" onClick={cancelSearch} title="Cancel search">
                  <MaterialIcon name="close" className="keyvault_btn_icon" /> Cancel
                </Button>
              ) : null}
            </form>

            <div className="keyvault_table_wrap">
              {searching ? (
                <div className="keyvault_empty">Searching the vault… this can take up to a minute.</div>
              ) : !hasSearched ? (
                <div className="keyvault_empty">Search for a secret key to begin.</div>
              ) : filteredLeft.length === 0 ? (
                <div className="keyvault_empty">No secrets match your search.</div>
              ) : (
                <table className="keyvault_table">
                  <thead>
                    <tr>
                      <th>Key</th>
                      <th>Value</th>
                      <th className="keyvault_col_actions" aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLeft.map((s) => {
                      const isShown = revealed[s.key] !== undefined;
                      return (
                        <tr key={s.key}>
                          <td className="keyvault_key" title={s.key}>
                            {s.key}
                          </td>
                          <td className={`keyvault_value ${isShown ? "" : "masked"}`}>
                            {isShown ? s_display(revealed[s.key]) : MASK}
                          </td>
                          <td className="keyvault_col_actions">
                            <div className="keyvault_row_actions">
                              <MaterialIcon
                                name={isShown ? "visibility_off" : "visibility"}
                                className="keyvault_icon"
                                style={{ cursor: "pointer" }}
                                onClick={() => toggleShowOne(s.key)}
                                title={isShown ? "Hide value" : "Show value"}
                              />
                              {isShown && revealed[s.key] ? <CopyToClipboard text={revealed[s.key]} /> : null}
                              <Button variant="plain" onClick={() => moveRight(s.key)} title="Move to edit">
                                Move <MaterialIcon name="arrow_forward" className="keyvault_btn_icon" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* RIGHT — edit & push */}
          <div className="keyvault_panel">
            <div className="keyvault_panel_head">
              <span className="keyvault_panel_title">
                Edit &amp; Push
                <span className="keyvault_count">{rightItems.length}</span>
                {dirtyCount > 0 ? <span className="keyvault_count dirty">{dirtyCount} modified</span> : null}
              </span>
              <div className="keyvault_row_actions">
                <Button variant="secondary" onClick={addNewSecret} title="Add a new secret">
                  <MaterialIcon name="add" className="keyvault_btn_icon" /> Add secret
                </Button>
                <Button variant="primary" onClick={pushChanges} isLoading={pushing} disabled={dirtyCount === 0}>
                  <MaterialIcon name="cloud_upload" className="keyvault_btn_icon" /> Push changes
                </Button>
              </div>
            </div>

            <div className="keyvault_list">
              {rightItems.length === 0 ? (
                <div className="keyvault_empty">Move a secret here to edit it, or add a new one.</div>
              ) : (
                rightItems.map((i) => {
                  const dirty = isPending(i);
                  return (
                    <div
                      className={`keyvault_row keyvault_row_edit ${dirty ? "dirty" : ""} ${i.isNew ? "is_new" : ""}`}
                      key={i.id}
                    >
                      <div className="keyvault_edit_head">
                        {i.isNew ? (
                          <Input
                            value={i.key}
                            onChange={(e) => editKey(i.id, e.target.value)}
                            placeholder="FULL-SECRET-NAME (include the prefix)"
                            containerStyle={{ width: "100%" }}
                          />
                        ) : (
                          <span className="keyvault_key" title={i.key}>
                            {i.key}
                            {dirty ? <span className="keyvault_dirty"> • modified</span> : null}
                          </span>
                        )}
                        <div className="keyvault_row_actions">
                          {i.isNew ? <span className="keyvault_new_tag">New</span> : null}
                          {!i.isNew && dirty ? (
                            <MaterialIcon
                              name="undo"
                              className="keyvault_icon"
                              style={{ cursor: "pointer" }}
                              onClick={() => resetRight(i.id)}
                              title="Revert"
                            />
                          ) : null}
                          <MaterialIcon
                            name="close"
                            className="keyvault_icon"
                            style={{ cursor: "pointer" }}
                            onClick={() => removeRight(i.id)}
                            title={i.isNew ? "Discard new secret" : "Remove from edit list"}
                          />
                        </div>
                      </div>
                      <Input
                        value={i.value}
                        onChange={(e) => editValue(i.id, e.target.value)}
                        placeholder="Enter value"
                        containerStyle={{ width: "100%" }}
                      />
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      ) : null}

      {activeTab === "history" ? (
        <div className="keyvault_history">
          <div className="keyvault_panel_head">
            <span className="keyvault_panel_title">
              Change History
              <span className="keyvault_count">{auditMeta.total_count}</span>
            </span>
            <form className="keyvault_search keyvault_search_inline" onSubmit={runAuditSearch}>
              <Input
                placeholder="Search by key, email or name…"
                value={auditSearch}
                onChange={(e) => setAuditSearch(e.target.value)}
                containerStyle={{ width: "100%" }}
              />
              <Button type="submit" variant="primary" disabled={auditLoading}>
                <MaterialIcon name="search" className="keyvault_btn_icon" /> Search
              </Button>
              <Button
                type="button"
                variant="tertiary"
                onClick={() => fetchAuditLogs(auditPage, auditSearch)}
                isLoading={auditLoading}
                title="Refresh"
              >
                <MaterialIcon name="refresh" className="keyvault_btn_icon" />
              </Button>
            </form>
          </div>
          <div className="keyvault_history_table_wrap">
            <table className="keyvault_history_table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Secret</th>
                  <th>Changed by</th>
                  <th>IP</th>
                  <th>Old version</th>
                  <th>New version</th>
                </tr>
              </thead>
              <tbody>
                {auditLoading ? (
                  <tr>
                    <td colSpan={6} className="keyvault_history_empty">
                      Loading history…
                    </td>
                  </tr>
                ) : auditLogs.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="keyvault_history_empty">
                      No changes recorded yet
                    </td>
                  </tr>
                ) : (
                  auditLogs.map((logItem) => (
                    <tr key={logItem.id || `${logItem.secret_key}-${logItem.created_at}`}>
                      <td>{formatDateTime(logItem.created_at)}</td>
                      <td className="keyvault_history_key">{logItem.secret_key}</td>
                      <td>
                        {logItem.performed_by_email || logItem.performed_by_name || logItem.performed_by_id || "—"}
                      </td>
                      <td>{logItem.ip_address || "—"}</td>
                      <td title={logItem.old_version || "(new secret)"}>{shortVersion(logItem.old_version)}</td>
                      <td title={logItem.new_version}>{shortVersion(logItem.new_version)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {auditMeta.total_pages > 1 ? (
            <div className="keyvault_pagination">
              <Button
                variant="tertiary"
                disabled={auditPage <= 1 || auditLoading}
                onClick={() => goAuditPage(auditPage - 1)}
              >
                <MaterialIcon name="chevron_left" className="keyvault_btn_icon" /> Prev
              </Button>
              <span className="keyvault_page_info">
                Page {auditMeta.page} of {auditMeta.total_pages} · {auditMeta.total_count} total
              </span>
              <Button
                variant="tertiary"
                disabled={auditPage >= auditMeta.total_pages || auditLoading}
                onClick={() => goAuditPage(auditPage + 1)}
              >
                Next <MaterialIcon name="chevron_right" className="keyvault_btn_icon" />
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
};

// Render an empty value distinctly without breaking layout.
const s_display = (value) => (value === "" ? "(empty)" : value);

export default KeyVaultSection;
